import React, { useMemo, useState } from "react";
import { Badge, Box, Button, ButtonGroup, InlineStack, Link, Modal, Text } from "@shopify/polaris";
import { ExportIcon } from "@shopify/polaris-icons";
import { getIncomingPurchaseQty, getShortageBeforePurchaseOrders, getToOrderQty, getTotalQtyNeeded, getTotalStock, getUsedInParentAssemblies, isLeafRow, ProcurementRow } from "../utils/procurement";

type DesignerPart = {
  PartID: number;
  ProductNameNL?: string | null;
  Productref?: string | null;
  SupplierID?: number | null;
  SupplierName?: string | null;
  SupplierNumber?: string | null;
  Unit?: string | null;
  EenheidAantal?: number | null;
  PurchasePrice?: number | null;
  PurchaseCurrency?: string | null;
  IncomingPurchaseQty?: number | null;
  Rack?: number | string | null;
  Legplank?: number | string | null;
};

type ProcurementLine = {
  partId: number;
  productName: string;
  productRef: string;
  supplierId: number | null;
  supplierName: string;
  supplierNumber: string;
  unit: string;
  unitQty: number | null;
  purchasePrice: number | null;
  purchaseCurrency: string;
  estimatedValue: number | null;
  incomingPurchaseQty: number;
  toOrderQty: number;
  estimatedToOrderValue: number | null;
  rack: number | string | null;
  shelf: number | string | null;
  totalQuantity: number;
  neededQty: number;
  warehouseQty: number;
  trianvalQty: number;
  usedInParentAssemblies: number;
  totalStock: number;
  shortageQty: number;
};

type ProcurementGroup = {
  supplierName: string;
  supplierNumber: string;
  supplierId: number | null;
  lines: ProcurementLine[];
  totalShortageQty: number;
  totalIncomingPurchaseQty: number;
  totalToOrderQty: number;
  totalEstimatedValue: number;
  totalEstimatedToOrderValue: number;
  currency: string;
};

type ProcurementPlannerModalProps = {
  open: boolean;
  onClose: () => void;
  data: ProcurementRow[];
  parts: DesignerPart[];
  forecastedQty: number;
};

type PlannerMode = "ordering" | "recount";

const formatQty = (value: number | null | undefined) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "-";
  return Number.isInteger(numberValue) ? String(numberValue) : numberValue.toFixed(2);
};

const formatOptional = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const getCurrencyCode = (currency: string | null | undefined) => {
  const normalizedCurrency = String(currency || "EUR").trim().toUpperCase();
  if (normalizedCurrency === "€" || normalizedCurrency === "EURO") return "EUR";
  if (/^[A-Z]{3}$/.test(normalizedCurrency)) return normalizedCurrency;
  return "EUR";
};

const formatMoney = (value: number | null | undefined, currency = "EUR") => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "-";
  return new Intl.NumberFormat("nl-BE", { style: "currency", currency: getCurrencyCode(currency) }).format(numberValue);
};

const normalizeLocationNumber = (value: string | number | null | undefined) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const compareLocationValue = (first: string | number | null | undefined, second: string | number | null | undefined) => {
  if ((first === null || first === undefined || first === "") && (second === null || second === undefined || second === "")) return 0;
  if (first === null || first === undefined || first === "") return 1;
  if (second === null || second === undefined || second === "") return -1;

  const firstNumber = normalizeLocationNumber(first);
  const secondNumber = normalizeLocationNumber(second);
  if (firstNumber !== null && secondNumber !== null) return firstNumber - secondNumber;
  return String(first).localeCompare(String(second), undefined, { numeric: true });
};

const compareLocation = (first: ProcurementLine, second: ProcurementLine) => {
  const rackCompare = compareLocationValue(first.rack, second.rack);
  if (rackCompare !== 0) return rackCompare;
  const shelfCompare = compareLocationValue(first.shelf, second.shelf);
  if (shelfCompare !== 0) return shelfCompare;
  return first.productName.localeCompare(second.productName);
};

const buildSupplierGroups = (procurementLines: ProcurementLine[], mode: PlannerMode) => {
  const groupsBySupplier = new Map<string, ProcurementGroup>();

  procurementLines.forEach((line) => {
    const supplierKey = `${line.supplierId || "missing"}-${line.supplierName}`;
    const existingGroup = groupsBySupplier.get(supplierKey) || {
      supplierName: line.supplierName,
      supplierNumber: line.supplierNumber,
      supplierId: line.supplierId,
      lines: [],
      totalShortageQty: 0,
      totalIncomingPurchaseQty: 0,
      totalToOrderQty: 0,
      totalEstimatedValue: 0,
      totalEstimatedToOrderValue: 0,
      currency: line.purchaseCurrency,
    };

    existingGroup.lines.push(line);
    existingGroup.totalShortageQty += line.shortageQty;
    existingGroup.totalIncomingPurchaseQty += line.incomingPurchaseQty;
    existingGroup.totalToOrderQty += line.toOrderQty;
    existingGroup.totalEstimatedValue += line.estimatedValue || 0;
    existingGroup.totalEstimatedToOrderValue += line.estimatedToOrderValue || 0;
    groupsBySupplier.set(supplierKey, existingGroup);
  });

  return Array.from(groupsBySupplier.values())
    .map((group) => ({
      ...group,
      lines: [...group.lines].sort(mode === "recount" ? compareLocation : (a, b) => a.productName.localeCompare(b.productName)),
    }))
    .sort((a, b) => a.supplierName.localeCompare(b.supplierName));
};

const escapeXml = (value: string | number | null | undefined) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const excelNumber = (value: number | null | undefined) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const excelCell = (value: string | number | null | undefined, styleId?: string, numeric = false) => {
  const attributes = styleId ? ` ss:StyleID="${styleId}"` : "";
  const numberValue = numeric ? excelNumber(value as number) : null;
  if (numeric && numberValue !== null) return `<Cell${attributes}><Data ss:Type="Number">${numberValue}</Data></Cell>`;
  return `<Cell${attributes}><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`;
};

const excelRow = (cells: string[]) => `<Row>${cells.join("")}</Row>`;

const buildWorksheetRows = (
  groups: ProcurementGroup[],
  title: string,
  forecastedQty: number,
  totalShortageQty: number,
  totalIncomingPurchaseQty: number,
  totalToOrderQty: number,
  totalEstimatedValue: number,
  totalEstimatedToOrderValue: number
) => {
  const rows = [
    excelRow([excelCell(title, "Title")]),
    excelRow([excelCell("Forecast"), excelCell(forecastedQty, undefined, true)]),
    excelRow([excelCell("Total shortage before purchase orders"), excelCell(totalShortageQty, undefined, true)]),
    excelRow([excelCell("Total on purchase order"), excelCell(totalIncomingPurchaseQty, undefined, true)]),
    excelRow([excelCell("Total to order after purchase orders"), excelCell(totalToOrderQty, undefined, true)]),
    excelRow([excelCell("Total estimated value"), excelCell(totalEstimatedValue, undefined, true), excelCell("EUR")]),
    excelRow([excelCell("Total estimated to order after purchase orders"), excelCell(totalEstimatedToOrderValue, undefined, true), excelCell("EUR")]),
    "<Row />",
    excelRow([
      excelCell("Supplier", "Header"),
      excelCell("Supplier number", "Header"),
      excelCell("Rack", "Header"),
      excelCell("Shelf", "Header"),
      excelCell("Part", "Header"),
      excelCell("Description", "Header"),
      excelCell("Supplier ref", "Header"),
      excelCell("Unit", "Header"),
      excelCell("Price", "Header"),
      excelCell("Currency", "Header"),
      excelCell("Total/model", "Header"),
      excelCell("Needed", "Header"),
      excelCell("WH", "Header"),
      excelCell("TR", "Header"),
      excelCell("In assemblies", "Header"),
      excelCell("Total stock", "Header"),
      excelCell("On PO", "Header"),
      excelCell("Shortage", "Header"),
      excelCell("To order", "Header"),
      excelCell("Est. value", "Header"),
      excelCell("Est. to order", "Header"),
    ]),
  ];

  groups.forEach((group) => {
    rows.push(
      excelRow([
        excelCell(group.supplierName, "Supplier"),
        excelCell(group.supplierNumber, "Supplier"),
        excelCell(""),
        excelCell(""),
        excelCell(""),
        excelCell(`${group.lines.length} parts`, "Supplier"),
        excelCell(""),
        excelCell("Supplier total", "Supplier"),
        excelCell(""),
        excelCell(getCurrencyCode(group.currency), "Supplier"),
        excelCell(""),
        excelCell(""),
        excelCell(""),
        excelCell(""),
        excelCell(""),
        excelCell(""),
        excelCell(""),
        excelCell(group.totalIncomingPurchaseQty, "Supplier", true),
        excelCell(group.totalShortageQty, "Supplier", true),
        excelCell(group.totalToOrderQty, "Supplier", true),
        excelCell(group.totalEstimatedValue, "Supplier", true),
        excelCell(group.totalEstimatedToOrderValue, "Supplier", true),
      ])
    );

    group.lines.forEach((line) => {
      rows.push(
        excelRow([
          excelCell(line.supplierName),
          excelCell(line.supplierNumber),
          excelCell(line.rack),
          excelCell(line.shelf),
          excelCell(line.partId, undefined, true),
          excelCell(line.productName),
          excelCell(line.productRef),
          excelCell(line.unit),
          excelCell(line.purchasePrice, undefined, true),
          excelCell(getCurrencyCode(line.purchaseCurrency)),
          excelCell(line.totalQuantity, undefined, true),
          excelCell(line.neededQty, undefined, true),
          excelCell(line.warehouseQty, undefined, true),
          excelCell(line.trianvalQty, undefined, true),
          excelCell(line.usedInParentAssemblies, undefined, true),
          excelCell(line.totalStock, undefined, true),
          excelCell(line.incomingPurchaseQty, undefined, true),
          excelCell(line.shortageQty, undefined, true),
          excelCell(line.toOrderQty, undefined, true),
          excelCell(line.estimatedValue, undefined, true),
          excelCell(line.estimatedToOrderValue, undefined, true),
        ])
      );
    });
  });

  return rows.join("");
};

const buildWorkbook = (
  procurementLines: ProcurementLine[],
  forecastedQty: number,
  totalShortageQty: number,
  totalIncomingPurchaseQty: number,
  totalToOrderQty: number,
  totalEstimatedValue: number,
  totalEstimatedToOrderValue: number
) => {
  const orderingGroups = buildSupplierGroups(procurementLines, "ordering");
  const recountGroups = buildSupplierGroups(procurementLines, "recount");

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Title"><Font ss:Bold="1" ss:Size="14" /></Style>
  <Style ss:ID="Header"><Font ss:Bold="1" /><Interior ss:Color="#D9EAF7" ss:Pattern="Solid" /></Style>
  <Style ss:ID="Supplier"><Font ss:Bold="1" /><Interior ss:Color="#F6F6F7" ss:Pattern="Solid" /></Style>
 </Styles>
 <Worksheet ss:Name="Ordering"><Table>${buildWorksheetRows(orderingGroups, "Ordering", forecastedQty, totalShortageQty, totalIncomingPurchaseQty, totalToOrderQty, totalEstimatedValue, totalEstimatedToOrderValue)}</Table></Worksheet>
 <Worksheet ss:Name="Recount inventory"><Table>${buildWorksheetRows(recountGroups, "Recount inventory", forecastedQty, totalShortageQty, totalIncomingPurchaseQty, totalToOrderQty, totalEstimatedValue, totalEstimatedToOrderValue)}</Table></Worksheet>
</Workbook>`;
};

const downloadWorkbook = (workbookXml: string, filename: string) => {
  const blob = new Blob([workbookXml], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default function ProcurementPlannerModal({ open, onClose, data, parts, forecastedQty }: ProcurementPlannerModalProps) {
  const [mode, setMode] = useState<PlannerMode>("ordering");

  const procurementLines = useMemo(() => {
    const partsById = new Map<number, DesignerPart>();
    parts.forEach((part) => partsById.set(Number(part.PartID), part));

    const linesByPart = new Map<number, ProcurementLine>();
    data.forEach((row: any) => {
      if (!isLeafRow(row)) return;

      const shortageQty = getShortageBeforePurchaseOrders(row, forecastedQty);
      if (shortageQty <= 0) return;

      const partId = Number(row.PartId);
      if (linesByPart.has(partId)) return;

      const part = partsById.get(partId);
      const purchasePrice = part?.PurchasePrice ?? null;
      const incomingPurchaseQty = getIncomingPurchaseQty(part);
      const toOrderQty = getToOrderQty(shortageQty, incomingPurchaseQty);
      const estimatedValue = purchasePrice === null ? null : shortageQty * Number(purchasePrice);
      const estimatedToOrderValue = purchasePrice === null ? null : toOrderQty * Number(purchasePrice);

      linesByPart.set(partId, {
        partId,
        productName: part?.ProductNameNL || "Unknown part",
        productRef: part?.Productref || "",
        supplierId: part?.SupplierID || null,
        supplierName: part?.SupplierName || "No supplier",
        supplierNumber: part?.SupplierNumber || "",
        unit: part?.Unit || "",
        unitQty: part?.EenheidAantal || null,
        purchasePrice,
        purchaseCurrency: part?.PurchaseCurrency || "EUR",
        estimatedValue,
        incomingPurchaseQty,
        toOrderQty,
        estimatedToOrderValue,
        rack: part?.Rack ?? null,
        shelf: part?.Legplank ?? null,
        totalQuantity: Number(row.total_quantity) || 0,
        neededQty: getTotalQtyNeeded(row, forecastedQty),
        warehouseQty: Number(row.stock_warehouse) || 0,
        trianvalQty: Number(row.stock_trianval) || 0,
        usedInParentAssemblies: getUsedInParentAssemblies(row),
        totalStock: getTotalStock(row),
        shortageQty,
      });
    });

    return Array.from(linesByPart.values());
  }, [data, forecastedQty, parts]);

  const supplierGroups = useMemo(() => buildSupplierGroups(procurementLines, mode), [mode, procurementLines]);

  const lineCount = procurementLines.length;
  const totalShortageQty = procurementLines.reduce((sum, line) => sum + line.shortageQty, 0);
  const totalIncomingPurchaseQty = procurementLines.reduce((sum, line) => sum + line.incomingPurchaseQty, 0);
  const totalToOrderQty = procurementLines.reduce((sum, line) => sum + line.toOrderQty, 0);
  const totalEstimatedValue = procurementLines.reduce((sum, line) => sum + (line.estimatedValue || 0), 0);
  const totalEstimatedToOrderValue = procurementLines.reduce((sum, line) => sum + (line.estimatedToOrderValue || 0), 0);
  const handleExport = () => {
    const workbookXml = buildWorkbook(procurementLines, forecastedQty, totalShortageQty, totalIncomingPurchaseQty, totalToOrderQty, totalEstimatedValue, totalEstimatedToOrderValue);
    const date = new Date().toISOString().slice(0, 10);
    downloadWorkbook(workbookXml, `designer-procurement-${date}.xls`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Warehouse shortage planner (${formatQty(forecastedQty)} forecast)`}
      size="large"
      fullScreen
      secondaryActions={[{ content: "Close", onAction: onClose }]}
    >
      <Modal.Section>
        <Box paddingBlockEnd="400">
          <InlineStack align="space-between" blockAlign="center" gap="400">
            <InlineStack gap="300" blockAlign="center">
              <Badge tone="critical">{`${lineCount} shortage parts`}</Badge>
              <Text as="span" tone="subdued">{`${formatQty(totalShortageQty)} shortage before PO`}</Text>
              <Text as="span" tone="subdued">{`${formatQty(totalIncomingPurchaseQty)} already on PO`}</Text>
              <Text as="span" tone="subdued">{`${formatQty(totalToOrderQty)} to order`}</Text>
              <Text as="span" tone="subdued">{`${formatMoney(totalEstimatedToOrderValue)} estimated to order`}</Text>
            </InlineStack>
            <InlineStack gap="300" blockAlign="center">
              <Button icon={ExportIcon} onClick={handleExport} disabled={procurementLines.length === 0}>Export Excel</Button>
              <ButtonGroup variant="segmented">
                <Button pressed={mode === "ordering"} onClick={() => setMode("ordering")}>Ordering</Button>
                <Button pressed={mode === "recount"} onClick={() => setMode("recount")}>Recount inventory</Button>
              </ButtonGroup>
            </InlineStack>
          </InlineStack>
        </Box>

        {supplierGroups.length === 0 ? (
          <Box padding="400">
            <Text as="p">No red to-buy warehouse parts for this forecast.</Text>
          </Box>
        ) : (
          <div className="procurementPlanner">
            {supplierGroups.map((group) => (
              <section className="procurementSupplierGroup" key={`${group.supplierId || "missing"}-${group.supplierName}`}>
                <div className="procurementSupplierHeader">
                  <div>
                    <Text variant="headingMd" as="h2">{group.supplierName}</Text>
                    <Text as="p" tone="subdued">{`Supplier #${formatOptional(group.supplierNumber)} · ${group.lines.length} parts`}</Text>
                  </div>
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone="attention">{`${formatQty(group.totalShortageQty)} shortage`}</Badge>
                    <Badge>{`${formatQty(group.totalIncomingPurchaseQty)} on PO`}</Badge>
                    <Badge tone="critical">{`${formatQty(group.totalToOrderQty)} to order`}</Badge>
                    <Badge tone="info">{`${formatMoney(group.totalEstimatedToOrderValue, group.currency)} estimated`}</Badge>
                  </InlineStack>
                </div>

                <table className={`procurementTable procurementTable--${mode}`}>
                  <thead>
                    <tr>
                      <th>Rack</th>
                      <th>Shelf</th>
                      <th>Part</th>
                      <th>Description</th>
                      <th>Supplier ref</th>
                      <th>Unit</th>
                      <th>Price</th>
                      <th>Total/model</th>
                      <th>Needed</th>
                      <th>WH</th>
                      <th>TR</th>
                      <th>In assemblies</th>
                      <th>Total stock</th>
                      <th>On PO</th>
                      <th>Shortage</th>
                      <th>To order</th>
                      <th>Est. value</th>
                      <th>Est. to order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.lines.map((line) => (
                      <tr key={`${group.supplierName}-${line.partId}`}>
                        <td className="procurementLocationCell">{formatOptional(line.rack)}</td>
                        <td className="procurementLocationCell">{formatOptional(line.shelf)}</td>
                        <td><Link removeUnderline url={`/products/${line.partId}`}>{`#${line.partId}`}</Link></td>
                        <td>{line.productName}</td>
                        <td>{formatOptional(line.productRef)}</td>
                        <td>{formatOptional(line.unit)}</td>
                        <td className="procurementNumberCell">{formatMoney(line.purchasePrice, line.purchaseCurrency)}</td>
                        <td className="procurementNumberCell">{formatQty(line.totalQuantity)}</td>
                        <td className="procurementNumberCell">{formatQty(line.neededQty)}</td>
                        <td className="procurementNumberCell">{formatQty(line.warehouseQty)}</td>
                        <td className="procurementNumberCell">{formatQty(line.trianvalQty)}</td>
                        <td className="procurementNumberCell">{formatQty(line.usedInParentAssemblies)}</td>
                        <td className="procurementNumberCell">{formatQty(line.totalStock)}</td>
                        <td className="procurementNumberCell procurementIncomingCell">{formatQty(line.incomingPurchaseQty)}</td>
                        <td className="procurementNumberCell procurementToBuyCell">{formatQty(line.shortageQty)}</td>
                        <td className="procurementNumberCell procurementToOrderCell">{formatQty(line.toOrderQty)}</td>
                        <td className="procurementNumberCell procurementEstimatedValueCell">{formatMoney(line.estimatedValue, line.purchaseCurrency)}</td>
                        <td className="procurementNumberCell procurementEstimatedValueCell">{formatMoney(line.estimatedToOrderValue, line.purchaseCurrency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        )}
      </Modal.Section>
    </Modal>
  );
}
